/**
 * Contact list configuration.
 * Extracted from ContactsList.tsx to enable generic listing component.
 *
 * Uses declarative column definitions with kind field:
 * - kind: 'object_name' - Renders avatar + display name together
 * - kind: 'property' - Renders property value using PropertyValueLabel
 * - kind: 'association' - Renders associated objects using CrmObjectTagGroup
 */

import type { ListPageConfig } from '../../core/types';
import { getEntityTypeCreateConfig } from '../../record/registry/entityTypeRegistry';

const createConfig = getEntityTypeCreateConfig('ahoy_contact');

export const contactListConfig: ListPageConfig = {
  entityType: 'ahoy_contact',
  title: 'Contact',
  pluralTitle: 'Contacts',

  // Column configuration for table view - declarative format
  // Labels are derived from property/association schemas
  // Order prioritizes "who is this person" context: Name → Company → Job Title
  columns: [
    { kind: 'object_name', sortable: true, width: 280 },
    { kind: 'association', name: 'ahoy_contact_to_company' },
    { kind: 'property', name: 'ahoy_job_title' },
    { kind: 'property', name: 'ahoy_email' },
    { kind: 'property', name: 'ahoy_phone_number' },
    { kind: 'association', name: 'ahoy_contact_to_lifecycle_stage' },
    { kind: 'property', name: 'ahoy_updated_at' },
  ],

  // Properties and associations from registry (single source of truth)
  createProperties: createConfig?.createProperties ?? [],
  createAssociations: createConfig?.createAssociations ?? [],

  // Metrics configuration
  metrics: [],

  // Default filters
  defaultFilters: {
    page: 1,
    page_size: 20,
    sort: 'ahoy_created_at',
    order: 'desc',
  },

  // Contacts are pipelineable via their lifecycle-stage association, but this
  // static config keeps `defaultView: 'table'` so the list opens as a table.
  // `ContactsList.tsx` sets `stageAssociationTypeName` (=
  // 'ahoy_contact_to_lifecycle_stage') on the runtime config, which is the
  // single switch enabling the Board CTA + `?view=board` pipeline route. It is
  // omitted from this static base only because the field is added there; the
  // lifecycle-stage column (driven by `columns`) and bulk stage-change actions
  // are independent of it.
  defaultView: 'table',

  // UI configuration
  showTimeFilter: false,
};
